<template>
  <li
    class="item"
    :class="printItem ? 'print' : 'digital'"
  >
    <article class="pb-15 md:pb-6">
      <!--displayed photos-->
      <template v-if="order.status !== 'Awaiting Payment' && item.thumbnail?.url">
        <div class="relative w-max min-w-52 max-w-52 h-34 md:h-38">
          <template v-if="item.type === PhotoType.Single">
            <img
              :src="item.thumbnail.url"
              class="h-full"
            >
          </template>

          <template v-else>
            <img
              v-for="i in 3"
              :key="i"
              :src="item.thumbnail.url"
              class="first:top-0 first:left-0 [&:nth-child(2)]:top-2 [&:nth-child(2)]:absolute [&:nth-child(2)]:left-2 last:absolute last:top-4 last:left-4 h-11/12 max-w-full md:max-w-11/12 pr-5 md:pr-0"
            >
          </template>
        </div>
      </template>

      <template v-else>
        <div class="w-max relative min-w-52 max-w-52">
          <template v-if="item.type === PhotoType.Single">
            <PhotoThumbnailPlaceholder />
          </template>

          <template v-else>
            <PhotoThumbnailPlaceholder
              v-for="i in 3"
              :key="i"
              class="first:top-0 first:left-0 [&:nth-child(2)]:top-2 [&:nth-child(2)]:absolute [&:nth-child(2)]:left-2 last:absolute last:top-4 last:left-4 h-11/12 pr-4 -ml-2 mr-2 -mt-3 scale-[90%]"
            />
          </template>
        </div>
      </template>

      <!-- Photo Details -->
      <section class="details-container">
        <h2>
          {{ item.event.name }}
          <span v-if="bibNumber">{{ bibNumber }}</span>
        </h2>

        <p class="metadata">
          <span class="text-sm">{{ productSize }}{{ productDetails }}</span>
        </p>

        <template v-if="order.status === 'Processing'">
          <template v-if="orderStatusInfo">
            <div class="flex gap-1 mt-1">
              <FontAwesomeIcon
                :icon="orderStatusInfo.icon"
                class="text-secondary"
                fixed-width
                v-bind="{ spin: orderStatusInfo?.spin }"
              />

              <span class="text-sm text-secondary">{{ orderStatusInfo.text }}</span>
            </div>
          </template>
        </template>
      </section>

      <footer>
        <div class="flex gap-2">
          <template v-if="authStore.user?.isAdmin && item.type === PhotoType.Single">
            <a
              class="btn btn-solid btn-secondary rounded-none text-sm"
              :href="item.originalPhoto || ''"
            >
              Download Original Photo
            </a>
          </template>

          <a
            class="btn btn-download btn-solid rounded-none"
            :class="!item.downloadableFile || order.status === 'Awaiting Payment' ? 'disabled' : 'btn-secondary'"
            :href="item.downloadableFile || ''"
          >
            Download
          </a>
        </div>
      </footer>
    </article>
  </li>
</template>

<script setup>
import { computed } from 'vue';

import { PhotoThumbnailPlaceholder } from '@/components/placeholders';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleInfo, faHourglassClock, faSpinner } from '@fortawesome/pro-solid-svg-icons';

import { DeliveryType, PhotoType, StyleType } from '@/const';
import { useAuthStore } from '@/store';

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  order: {
    type: Object,
    default: () => ({}),
  },
});

const authStore = useAuthStore();

const printItem = computed(() => {
  return props.item.delivery === DeliveryType.Print;
});

const singleItem = computed(() => {
  return props.item.type === PhotoType.Single;
});

const bibNumber = computed(() => {
  return props.item.bibNumber ? '| Bib no. ' + props.item.bibNumber : '';
});

const productSize = computed(() => {
  const sizes = {
    'Digital Standard': 'Standard digital download',
    'Digital High Definition': 'High Definition digital download',
    'Print Standard': 'Standard Gloss Print',
    'Print Jumbo': 'Jumbo Gloss Print',
    'Print A4': 'A4 Gloss print',
    'Print A3 Canvas': 'A3 Canvas Print',
  };

  if (singleItem.value) {
    return sizes[props.item.size];
  }

  return 'Standard digital download';
});

const productDetails = computed(() => {
  return props.item.style === StyleType.Event ? ', with event details' : props.item.style === StyleType.RaceTime ? ', with race time' : '';
});

const orderStatusInfo = computed(() => {
  let orderStatusInfo = {};

  switch (true) {
    case props.item.downloadableFile === null: {
      orderStatusInfo = {
        text: 'Processing your photo',
        icon: faSpinner,
        spin: true,
      };
      return orderStatusInfo;
    }
    case props.item.delivery === DeliveryType.Print: {
      orderStatusInfo = {
        text: 'Sent for printing. Standard digital download available immediately.',
        icon: faCircleInfo,
      };
      return orderStatusInfo;
    }
    case props.item.size === 'Digital High Definition' && props.item.megapixels < 5.00: {
      orderStatusInfo = {
        text: 'Ready in 2-3 business days. Standard digital download available immediately.',
        icon: faHourglassClock,
      };
      return orderStatusInfo;
    }
  }
  return orderStatusInfo;
});
</script>
